import React, { useRef, useState, DragEvent } from 'react';
import { Button, ProgressBar } from 'react-bootstrap';
import {
  CloudArrowUp,
  X,
  FileEarmarkText,
  FileEarmarkPdf,
  FileEarmarkImage,
} from 'react-bootstrap-icons';
import './SourceUploader.css';

const getFileIcon = (fileType: string) => {
  if (fileType === 'application/pdf') return <FileEarmarkPdf size={14} />;
  if (fileType.startsWith('image/')) return <FileEarmarkImage size={14} />;
  return <FileEarmarkText size={14} />;
};

const ALLOWED_TYPES = [
  'application/pdf',
  'text/plain',
  'text/markdown',
  'image/png',
  'image/jpeg',
];

const ALLOWED_EXTENSIONS = '.pdf,.txt,.md,.png,.jpg,.jpeg';

const EXTENSION_MIME_MAP: Record<string, string> = {
  pdf: 'application/pdf',
  txt: 'text/plain',
  md: 'text/markdown',
  markdown: 'text/markdown',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
};

const resolveFileType = (file: File): string => {
  if (file.type && ALLOWED_TYPES.includes(file.type)) return file.type;
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  return EXTENSION_MIME_MAP[ext] ?? file.type;
};

interface PendingFile {
  file: File;
  name: string;
  type: string;
  size: number;
}

interface SourceUploaderProps {
  onFilesSelected: (files: PendingFile[]) => void;
  maxFiles?: number;
  maxFileSize?: number;
  disabled?: boolean;
  limitReachedMessage?: React.ReactNode;
}

const SourceUploader: React.FC<SourceUploaderProps> = ({
  onFilesSelected,
  maxFiles = 5,
  maxFileSize = 5 * 1024 * 1024,
  disabled = false,
  limitReachedMessage,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);
  const [errors, setErrors] = useState<React.ReactNode[]>([]);

  const validateAndAddFiles = (fileList: FileList | File[]) => {
    const files = Array.from(fileList);
    const newErrors: React.ReactNode[] = [];
    const validFiles: PendingFile[] = [];

    for (const file of files) {
      if (pendingFiles.length + validFiles.length >= maxFiles) {
        newErrors.push(limitReachedMessage ?? `Maximum ${maxFiles} files allowed`);
        break;
      }

      const resolvedType = resolveFileType(file);
      if (!ALLOWED_TYPES.includes(resolvedType)) {
        newErrors.push(`${file.name}: Unsupported file type. Use PDF, TXT, MD, PNG, or JPEG.`);
        continue;
      }

      if (file.size > maxFileSize) {
        const maxMB = Math.round(maxFileSize / (1024 * 1024));
        newErrors.push(`${file.name}: File exceeds ${maxMB}MB limit.`);
        continue;
      }

      validFiles.push({
        file,
        name: file.name,
        type: resolvedType,
        size: file.size,
      });
    }

    setErrors(newErrors);

    if (validFiles.length > 0) {
      const updated = [...pendingFiles, ...validFiles];
      setPendingFiles(updated);
      onFilesSelected(updated);
    }
  };

  const handleDragOver = (e: DragEvent) => {
    e.preventDefault();
    if (!disabled) setIsDragOver(true);
  };

  const handleDragLeave = (e: DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    if (!disabled && e.dataTransfer.files.length > 0) {
      validateAndAddFiles(e.dataTransfer.files);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      validateAndAddFiles(e.target.files);
      // Reset input so the same file can be selected again
      e.target.value = '';
    }
  };

  const removeFile = (index: number) => {
    const updated = pendingFiles.filter((_, i) => i !== index);
    setPendingFiles(updated);
    onFilesSelected(updated);
    setErrors([]);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="source-uploader">
      <div
        className={`upload-dropzone ${isDragOver ? 'drag-over' : ''} ${disabled ? 'disabled' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => !disabled && fileInputRef.current?.click()}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            !disabled && fileInputRef.current?.click();
          }
        }}
      >
        <CloudArrowUp size={32} className="upload-icon" />
        <p className="upload-text">
          Drag & drop files here, or <span className="upload-link">browse</span>
        </p>
        <p className="upload-hint">PDF, TXT, MD, PNG, JPEG (max {Math.round(maxFileSize / (1024 * 1024))}MB each)</p>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={ALLOWED_EXTENSIONS}
          onChange={handleFileInputChange}
          style={{ display: 'none' }}
        />
      </div>

      {errors.length > 0 && (
        <div className="upload-errors mt-2">
          {errors.map((err, i) => (
            <p key={i} className="text-danger small mb-1">{err}</p>
          ))}
        </div>
      )}

      {pendingFiles.length > 0 && (
        <div className="pending-files mt-3">
          {pendingFiles.map((pf, i) => (
            <div key={i} className="pending-file-item d-flex align-items-center justify-content-between mb-2">
              <div className="file-info">
                <span className="file-icon">{getFileIcon(pf.type)}</span>
                <span className="file-name">{pf.name}</span>
                <span className="file-size text-muted ms-2">{formatFileSize(pf.size)}</span>
              </div>
              <Button
                variant="link"
                size="sm"
                className="p-0 text-muted"
                onClick={(e) => {
                  e.stopPropagation();
                  removeFile(i);
                }}
              >
                <X size={16} />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SourceUploader;
